import {
  AccountScreen, AppShell, EmptyState, EntryOrNotFound, entryRoutes, NotificationCentre, opensElsewhere, Permissions,
  RequirePermission, RequireSession, useI18n, useSession, useTenantSettings, type NavItem,
} from '@daghfal/core';
import { Route, Routes } from 'react-router-dom';
import { Child } from './routes/Child';
import { Children } from './routes/Children';
import { AcademicYears } from './routes/AcademicYears';
import { Rollover } from './routes/Rollover';
import { AccountRequests } from './routes/AccountRequests';
import { Classes } from './routes/Classes';
import { Complaint } from './routes/Complaint';
import { Complaints } from './routes/Complaints';
import { Attendance } from './routes/Attendance';
import { Dashboard } from './routes/Dashboard';
import { Finance } from './routes/Finance';
import { NewChild } from './routes/NewChild';
import { Photos } from './routes/Photos';
import { ConsentGaps } from './routes/ConsentGaps';
import { RegistrationGaps } from './routes/RegistrationGaps';
import { DailyCare } from './routes/DailyCare';
import { ImportChildren } from './routes/ImportChildren';
import { IncidentPage } from './routes/Incident';
import { Reports } from './routes/Reports';
import { Settings } from './routes/Settings';
import { Staff } from './routes/Staff';
import { CONSOLE_ROUTES } from './routeMarks';

export function App() {
  const { t } = useI18n();
  const brand = { name: t('console.name'), tagline: t('console.tagline') };

  return (
    <Routes>
      {entryRoutes(brand, { slugs: true })}
      <Route path="/*" element={<RequireSession slugEntry><ConsoleShell /></RequireSession>} />
    </Routes>
  );
}

function ConsoleShell() {
  const { t, locale } = useI18n();
  const { session } = useSession();

  // Every date and amount on this console formats in the tenant's own timezone, currency and numerals
  // rather than the browser's (SRS §2.4, NFR-I18N-03).
  useTenantSettings();

  const nav: NavItem[] = [
    { to: '/', label: t('nav.dashboard'), icon: '📊', end: true },
    { to: '/children', label: t('nav.children'), icon: '👶', permission: Permissions.children.viewAll },
    { to: '/classes', label: t('nav.classes'), icon: '🧩', permission: Permissions.tenant.manageClasses },
    { to: '/academic-years', label: t('nav.years'), icon: '📅', permission: Permissions.academic.manage },
    { to: '/staff', label: t('nav.staff'), icon: '👩‍🏫', permission: Permissions.staff.view },
    // People the nursery confirms face to face so a held-back password reset can go ahead (Q25).
    { to: '/account-requests', label: t('nav.accountRequests'), icon: '🔑', permission: Permissions.accounts.recover },
    { to: '/daily', label: t('nav.daily'), icon: '📔', permission: Permissions.daily.record },
    { to: '/attendance', label: t('nav.attendance'), icon: '🗓️', permission: Permissions.attendance.viewAll },
    { to: '/photos', label: t('nav.photos'), icon: '📷', permission: Permissions.photos.upload },
    // Finance never appears for a role without `finance.view`. Staff must not see a financial field
    // anywhere in the product (FR-STF-11), and this console is reached by teachers in some tenants.
    { to: '/finance', label: t('nav.finance'), icon: '💰', permission: Permissions.finance.view },
    // Absent for a teacher, who holds no complaint permission at all — which is deliberate, since they
    // are who complaints are most often about (FR-CMP-03).
    { to: '/complaints', label: t('nav.complaints'), icon: '🗒️', permission: Permissions.complaints.view },
    { to: '/reports', label: t('nav.reports'), icon: '📈', permission: Permissions.tenant.viewReports },
    { to: '/settings', label: t('nav.settings'), icon: '⚙️', permission: Permissions.tenant.manageSettings },
    // Complaints, support-session requests and plan limits are raised to the people who run the setting,
    // and until now this console had nowhere to read them.
    { to: '/notifications', label: t('nav.notifications'), icon: '🔔' },
  ];

  const tenantName = (locale === 'en' && session?.membership.tenantNameEn)
    || session?.membership.tenantNameAr
    || t('console.name');

  if (session?.membership.scope !== 'Tenant') {
    // A platform employee's own session has no tenant. Reaching a nursery's data is a support session,
    // which is requested and approved separately (SRS §3.5) — it is not something this console grants.
    return (
      <AppShell brand={t('console.name')} nav={[]}>
        <EmptyState icon="⛔" title={t('error.forbidden')} tone="warning" />
      </AppShell>
    );
  }

  // Who the console is for is the server's rule (MembershipApps, decision 2026-10-16 #7): /auth/me names the apps
  // this membership opens, and the chooser greys it out here by the same list. Opened anyway, from a bookmark or
  // as someone's only membership, the console says where it opens instead of showing pages that all refuse.
  const elsewhere = opensElsewhere(session.membership, 'admin');
  if (elsewhere) {
    return (
      <AppShell brand={tenantName} nav={[]}>
        <div data-opens-in={elsewhere}>
          <EmptyState
            icon="↪" tone="warning" title={t(`auth.membership.opensIn.${elsewhere}`)}
            body={t('auth.membership.elsewhere.hint')}
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      brand={tenantName} context={session.membership.roleNameAr} nav={nav}
      notificationsPath="/notifications" watermarkRoutes={CONSOLE_ROUTES}
    >
      <Routes>
        <Route index element={<Dashboard />} />
        <Route
          path="children"
          element={
            <RequirePermission all={[Permissions.children.viewAll]}>
              <Children />
            </RequirePermission>
          }
        />
        <Route
          path="children/new"
          element={<RequirePermission all={[Permissions.children.create]}><NewChild /></RequirePermission>}
        />
        <Route
          path="children/import"
          element={<RequirePermission all={[Permissions.children.create]}><ImportChildren /></RequirePermission>}
        />
        <Route
          path="children/registration-gaps"
          element={<RequirePermission all={[Permissions.children.update]}><RegistrationGaps /></RequirePermission>}
        />
        <Route
          path="children/consent-gaps"
          element={<RequirePermission all={[Permissions.children.update]}><ConsentGaps /></RequirePermission>}
        />
        <Route
          path="children/:id"
          element={
            <RequirePermission all={[Permissions.children.viewAll]}>
              <Child />
            </RequirePermission>
          }
        />
        <Route
          path="classes"
          element={<RequirePermission all={[Permissions.tenant.manageClasses]}><Classes /></RequirePermission>}
        />
        <Route
          path="academic-years"
          element={<RequirePermission all={[Permissions.academic.manage]}><AcademicYears /></RequirePermission>}
        />
        <Route
          path="academic-years/:id/rollover"
          element={<RequirePermission all={[Permissions.academic.manage]}><Rollover /></RequirePermission>}
        />
        <Route
          path="staff"
          element={<RequirePermission all={[Permissions.staff.view]}><Staff /></RequirePermission>}
        />
        <Route
          path="account-requests"
          element={<RequirePermission all={[Permissions.accounts.recover]}><AccountRequests /></RequirePermission>}
        />
        <Route
          path="daily"
          element={<RequirePermission all={[Permissions.daily.record]}><DailyCare /></RequirePermission>}
        />
        <Route
          path="attendance"
          element={
            <RequirePermission all={[Permissions.attendance.viewAll]}>
              <Attendance />
            </RequirePermission>
          }
        />
        <Route
          path="photos"
          element={<RequirePermission all={[Permissions.photos.upload]}><Photos /></RequirePermission>}
        />
        <Route
          path="finance"
          element={<RequirePermission all={[Permissions.finance.view]}><Finance /></RequirePermission>}
        />
        <Route
          path="complaints"
          element={<RequirePermission all={[Permissions.complaints.view]}><Complaints /></RequirePermission>}
        />
        <Route
          path="complaints/:id"
          element={<RequirePermission all={[Permissions.complaints.view]}><Complaint /></RequirePermission>}
        />
        <Route path="reports" element={<Reports />} />
        <Route
          path="incidents/:id"
          element={<RequirePermission all={[Permissions.health.view]}><IncidentPage /></RequirePermission>}
        />
        <Route
          path="settings"
          element={
            <RequirePermission all={[Permissions.tenant.manageSettings]}><Settings /></RequirePermission>
          }
        />
        <Route path="notifications" element={<NotificationCentre />} />
        <Route path="account" element={<AccountScreen />} />
        <Route path="*" element={<EntryOrNotFound><EmptyState icon="🔍" title={t('error.notFound')} /></EntryOrNotFound>} />
      </Routes>
    </AppShell>
  );
}
